'use strict';

/**
 * Wishlist "Add All to Cart" functionality
 * Adds all wishlist items to the cart (both mini-cart and cart page),
 * then removes each added item from the wishlist.
 */
(function () {

  /**
   * Wait for the wishlist page to finish rendering (skeleton removed),
   * then initialise the button and show the action bar.
   */
  function waitForWishlistReady() {
    const wishlistSection = document.querySelector('.bls__wishlist-page-section');
    if (!wishlistSection) return;

    // Poll until the skeleton is removed and the inner div is visible
    const observer = new MutationObserver(function () {
      const inner = document.querySelector('.bls__wishlist-page-section-inner');
      if (inner && !inner.classList.contains('d-none')) {
        observer.disconnect();
        initAddAllButton();
      }
    });

    observer.observe(wishlistSection, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });

    // Also fire immediately in case the page already loaded
    const inner = document.querySelector('.bls__wishlist-page-section-inner');
    if (inner && !inner.classList.contains('d-none')) {
      initAddAllButton();
    }
  }

  /**
   * Show the action bar and bind the button click.
   */
  function initAddAllButton() {
    const actionBar = document.querySelector('.bls__wishlist-action-bar');
    const btn = document.getElementById('bls__add-all-to-cart');
    if (!actionBar || !btn) return;

    // Only show the bar when there are wishlist items
    const items = JSON.parse(localStorage.getItem('bls__wishlist-items') || '[]');
    if (items && items.length > 0) {
      actionBar.classList.remove('d-none');
    }

    btn.addEventListener('click', handleAddAllToCart);
  }

  /**
   * Collect all variant IDs from the wishlist product cards,
   * add them all to the cart via the Shopify Ajax API,
   * then remove them from the wishlist.
   */
  async function handleAddAllToCart() {
    const btn = document.getElementById('bls__add-all-to-cart');
    if (!btn || btn.disabled) return;

    // Collect variant IDs from every visible wishlist card
    const wishlistCards = document.querySelectorAll('.bls__wishlist-list');
    if (!wishlistCards.length) return;

    const variantItems = [];
    wishlistCards.forEach(function (card) {
      const variantEl = card.querySelector('[data-product-variant-id]');
      if (variantEl) {
        const variantId = parseInt(variantEl.dataset.productVariantId, 10);
        const productHandle = card.dataset.productHandle;
        if (variantId) {
          variantItems.push({ id: variantId, quantity: 1, handle: productHandle });
        }
      }
    });

    if (!variantItems.length) {
      alert('No available products to add to cart.');
      return;
    }

    // Show loading state
    setButtonLoading(btn, true);

    try {
      // Build items array for Shopify Cart API (add multiple at once)
      const itemsPayload = variantItems.map(function (v) {
        return { id: v.id, quantity: v.quantity };
      });

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ items: itemsPayload })
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.description || 'Could not add items to cart.');
      }

      // Successfully added — update cart UI
      await refreshCartUI();

      // Remove each added product from the wishlist
      variantItems.forEach(function (v) {
        if (v.handle) {
          removeFromWishlist(v.handle);
        }
      });

      // Open the cart drawer/notification if available
      const cartDrawer = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      if (cartDrawer && typeof cartDrawer.open === 'function') {
        cartDrawer.open();
      }

    } catch (error) {
      console.error('[Add All to Cart]', error);
      alert(error.message || 'An error occurred. Please try again.');
    } finally {
      setButtonLoading(btn, false);
    }
  }

  /**
   * Refresh cart count badges, free shipping bar, and mini-cart sections.
   */
  async function refreshCartUI() {
    try {
      const cartJson = await fetch('/cart.json').then(function (r) { return r.json(); });

      // Update cart count badges
      document.querySelectorAll('.cart-count').forEach(function (el) {
        if (el.classList.contains('cart-count-drawer')) {
          el.innerHTML = '(' + cartJson.item_count + ')';
        } else {
          el.innerHTML = cartJson.item_count;
        }
      });

      // Update header total price if element exists
      const headerTotal = document.querySelector('header-total-price');
      if (headerTotal && typeof headerTotal.updateTotal === 'function') {
        headerTotal.updateTotal(cartJson);
      }

      // Re-render mini-cart sections via Shopify section rendering API
      const cartDrawer = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
        const sectionIds = cartDrawer.getSectionsToRender().map(function (s) { return s.id; });
        const params = sectionIds.map(function (id) { return 'sections[]=' + encodeURIComponent(id); }).join('&');
        const sectionsResponse = await fetch('/cart.js?' + params + '&sections=' + sectionIds.join(','));
        // Actually use the Shopify Section Rendering API
        const sectionsUrl = '/?sections=' + sectionIds.join(',');
        const sectionsHtml = await fetch(sectionsUrl).then(function (r) { return r.json(); }).catch(function () { return null; });

        if (sectionsHtml) {
          cartDrawer.getSectionsToRender().forEach(function (section) {
            const el = document.getElementById(section.id);
            if (el && sectionsHtml[section.id]) {
              const parsed = new DOMParser().parseFromString(sectionsHtml[section.id], 'text/html');
              const miniCartForm = parsed.querySelector('#form-mini-cart');
              if (miniCartForm) {
                el.innerHTML = miniCartForm.innerHTML;
              }
            }
          });

          if (typeof cartDrawer.cartAction === 'function') {
            cartDrawer.cartAction();
          }
        }
      }

    } catch (e) {
      console.warn('[Add All to Cart] Could not refresh cart UI:', e);
    }
  }

  /**
   * Remove a product handle from the wishlist localStorage
   * and remove it from the DOM.
   */
  function removeFromWishlist(productHandle) {
    // Remove from localStorage
    const items = JSON.parse(localStorage.getItem('bls__wishlist-items') || '[]');
    const index = items.indexOf(productHandle);
    if (index > -1) {
      items.splice(index, 1);
      localStorage.setItem('bls__wishlist-items', JSON.stringify(items));
    }

    // Remove the card from the DOM
    const card = document.querySelector('.bls__wishlist-list[data-product-handle="' + productHandle + '"]');
    if (card) {
      card.remove();
    }

    // Show the "no products" message if the list is now empty
    const remaining = document.querySelectorAll('.bls__wishlist-list');
    if (remaining.length === 0) {
      const actionBar = document.querySelector('.bls__wishlist-action-bar');
      if (actionBar) actionBar.classList.add('d-none');

      const noProduct = document.querySelector('.bls__wishlist-no-product-js');
      if (noProduct) noProduct.classList.remove('d-none');
    }

    // Update wishlist header count badges
    if (typeof BlsWishlistHeader !== 'undefined' && typeof BlsWishlistHeader.init === 'function') {
      BlsWishlistHeader.init();
    }

    // Re-sync wishlist icons across the page
    if (typeof BlsSubActionProduct !== 'undefined' && typeof BlsSubActionProduct.handleInitWishlist === 'function') {
      BlsSubActionProduct.handleInitWishlist();
    }
  }

  let originalText = '';

  /**
   * Toggle the button between loading and normal states.
   */
  function setButtonLoading(btn, isLoading) {
    const textEl = btn.querySelector('.bls__add-all-text');
    const iconEl = btn.querySelector('.bls__add-all-icon');
    const loaderEl = btn.querySelector('.bls__add-all-loader');

    if (!originalText && textEl) {
      originalText = textEl.textContent.trim();
    }

    btn.disabled = isLoading;
    if (isLoading) {
      if (textEl) textEl.textContent = 'Adding…';
      if (iconEl) iconEl.style.display = 'none';
      if (loaderEl) loaderEl.style.display = 'inline-block';
    } else {
      if (textEl) textEl.textContent = originalText || 'Add All to Cart';
      if (iconEl) iconEl.style.display = 'inline-block';
      if (loaderEl) loaderEl.style.display = 'none';
    }
  }

  // Boot
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', waitForWishlistReady);
  } else {
    waitForWishlistReady();
  }

})();
